import React, { useRef, useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import {
  withStyles,
  Typography,
  Popover,
  CircularProgress,
  Tooltip,
  FormControl,
} from '@material-ui/core'
import { ChromePicker } from 'react-color'
import { useDispatch } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import styles from './styles'
import {
  CoreToggleButtonGroup,
  CoreToggleButton,
  CoreButton,
  CoreIconButton,
  CoreTextField,
  CoreRadioButton,
} from 'src/components/core'

import {
  BUTTON_TYPES,
  WINDOW_TYPES,
  handleDestinationChange,
  handleTextChange,
  handleColorChange,
  handleFileUpload,
  handleFileDelete,
  handleIconSelect,
  handleIconDelete,
  showIconSelector,
  deleteButton,
  getInitialButtonState,
  getModelElementLink,
  handleTargetChange,
  handleLinkChange,
} from '../utils'

const ButtonOptionsModal = props => {
  const { classes, editor, target, selection, position } = props
  const fileInputRef = useRef(null)
  const dispatch = useDispatch()

  const [destination, setDestination] = useState(BUTTON_TYPES.LINK)
  const [linkValue, setLink] = useState('')
  const [title, setTitle] = useState('')
  const [textColor, setTextColor] = useState('')
  const [pdfName, setPDF] = useState('')
  const [openIn, setOpenIn] = useState('_blank')
  const [selectedIcon, setSelectedIcon] = useState('')
  const [selectedIconName, setSelectedIconName] = useState(
    'lets-icons:add-round',
  )
  const [isLoading, setIsLoading] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [pickerVisibles, setPickerVisibles] = useState({
    fontColor: false,
    backgroundColor: false,
    cardBackgroundColor: false,
    buttonColor: false,
  })
  const [linkError, setLinkError] = useState('')

  useEffect(() => {
    const initialState = getInitialButtonState(selection)
    setSelectedIcon(initialState.selectedIcon)
    setSelectedIconName(initialState.selectedIconName)
    setLink(initialState.link)
    setOpenIn(initialState.targetvalue)
    setTextColor(initialState.textColor)
    setTitle(initialState.title)
    setPDF(initialState.pdfName)
    setDestination(initialState.destination)
  }, [selection])

  useEffect(() => {
    const currentLink = getModelElementLink(editor, target)
    if (currentLink) {
      setLink(currentLink)
    }
  }, [editor, target])

  const onDestinationChange = (event, newDestination) => {
    handleDestinationChange(newDestination, setDestination)
  }

  const openInOptions = [
    { value: '_blank', label: 'New tab' },
    { value: '_self', label: 'Current tab' },
  ]

  const onTextChange = event => {
    handleTextChange(event, {
      editor,
      position,
      linkValue,
      textColor,
      destination,
      pdfName,
      selectedIcon,
      selectedIconName,
      openIn,
      setTitle,
    })
  }

  const onColorChange = event => {
    handleColorChange(event, {
      editor,
      position,
      linkValue,
      title,
      destination,
      pdfName,
      selectedIcon,
      selectedIconName,
      openIn,
      setTextColor,
    })
  }

  const onHandleRadioChange = event => {
    setOpenIn(event)
    handleTargetChange(event, {
      editor,
      position,
      linkValue,
      textColor,
      title,
      destination,
      pdfName,
      selectedIcon,
      selectedIconName,
      setOpenIn,
    })
  }

  const validateLink = value => {
    if (!value) {
      setLinkError('')
      return true
    }

    if (
      value.startsWith('mailto:') ||
      value.startsWith('tel:') ||
      value.startsWith('#')
    ) {
      setLinkError('')
      return true
    }

    try {
      const urlObj = new URL(value)
      if (urlObj.protocol === 'https:' || urlObj.protocol === 'http:') {
        setLinkError('')
        return true
      }
      setLinkError('Invalid URL format')
      return false
    } catch (e) {
      setLinkError('Invalid URL format')
      return false
    }
  }

  const onLinkChange = event => {
    const value = event.target.value
    setLink(value)

    if (validateLink(value)) {
      handleLinkChange(event, {
        editor,
        position,
        textColor,
        title,
        pdfName,
        selectedIcon,
        selectedIconName,
        setLink,
        setPDF,
        openIn,
      })
    }
  }

  const onFileUpload = async e => {
    await handleFileUpload(e.target.files[0], {
      editor,
      position,
      textColor,
      title,
      selectedIcon,
      selectedIconName,
      setIsLoading,
      setPDF,
      setLink,
      openIn,
    })
  }

  const onFileDelete = () => {
    handleFileDelete({
      editor,
      position,
      textColor,
      title,
      destination,
      selectedIcon,
      selectedIconName,
      setPDF,
      setLink,
      openIn,
    })
  }

  const onIconSelect = icon => {
    handleIconSelect(icon, {
      editor,
      position,
      linkValue,
      textColor,
      title,
      destination,
      pdfName,
      setSelectedIcon,
      setSelectedIconName,
      openIn,
    })
  }

  const onIconDelete = () => {
    handleIconDelete({
      editor,
      position,
      linkValue,
      textColor,
      title,
      destination,
      pdfName,
      setSelectedIcon,
      setSelectedIconName,
      openIn,
    })
  }

  const onShowIconSelector = () => {
    const type = 0
    showIconSelector({
      editor,
      target,
      handleIconSelect: onIconSelect,
      dispatch,
      type,
    })
  }

  const onDeleteClick = () => {
    deleteButton(editor, position)
    dispatch(messageBoxHideAction())
  }

  return (
    <div className={classes.box}>
      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Button Title</Typography>
        </div>
        <div className={classes.textbox}>
          <CoreTextField
            className={classes.textfield}
            fullWidth
            variant="outlined"
            color="primary"
            size="small"
            value={title}
            onChange={onTextChange}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Open in</Typography>
        </div>
        <FormControl size="small">
          <CoreRadioButton
            options={openInOptions}
            value={openIn}
            size="small"
            onChange={event => onHandleRadioChange(event)}
          />
        </FormControl>
      </div>

      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Destination</Typography>
        </div>
        <CoreToggleButtonGroup
          value={destination}
          onChange={onDestinationChange}
          exclusive
          fullWidth
        >
          <CoreToggleButton value="link">
            <Typography className={classes.header}>Link</Typography>
          </CoreToggleButton>
          <CoreToggleButton value="pdf">
            <Typography className={classes.header}>File</Typography>
          </CoreToggleButton>
        </CoreToggleButtonGroup>
      </div>

      {destination === 'link' ? (
        <div className={classes.row}>
          <div className={classes.linkHeader}>
            <Typography className={classes.header}>Link</Typography>
            <Tooltip
              className={classes.tooltip}
              title={
                <Typography className={classes.tooltipText}>
                  Enter a link (e.g., <br />
                  https://example.com/), an email <br />
                  (mailto:), a phone number (tel:), or a <br />
                  page anchor (#section).
                </Typography>
              }
            >
              <Icon icon="material-symbols:help-outline-rounded" />
            </Tooltip>
          </div>
          <div className={classes.textbox}>
            <CoreTextField
              className={classes.textfield}
              fullWidth
              variant="outlined"
              color="primary"
              size="small"
              onChange={onLinkChange}
              value={linkValue}
              helperText={linkError}
              error={!!linkError}
              placeholder="Insert a link or mailto:email"
            />
          </div>
        </div>
      ) : (
        <div className={classes.row}>
          <div>
            <Typography className={classes.header}>File</Typography>
          </div>
          {pdfName === '' ? (
            <div>
              <div className={classes.textbox}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={onFileUpload}
                  className="hidden"
                  accept=".pdf"
                />
                <button
                  className={classes.fileButton}
                  onClick={() => fileInputRef.current.click()}
                >
                  Upload File
                </button>
              </div>
              <div>
                <Typography className={classes.message}>
                  Supported format: .PDF
                </Typography>
              </div>
            </div>
          ) : (
            <div>
              {!isLoading ? (
                <div>
                  <div className={classes.textboxDisable}>
                    <Icon
                      icon="lets-icons:file-fill"
                      width="16"
                      height="16"
                      color="green"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={onFileUpload}
                      className="hidden"
                      accept=".pdf"
                    />
                    <button
                      className={classes.fileButtonDisable}
                      onClick={() => fileInputRef.current.click()}
                    >
                      {pdfName}
                    </button>
                    <CoreIconButton
                      color="dark"
                      size="small"
                      variant="text"
                      onClick={onFileDelete}
                      className={classes.iconButtonTrash}
                    >
                      <Icon
                        icon="iconoir:trash"
                        width="16"
                        height="16"
                        color="#919EAB"
                      />
                    </CoreIconButton>
                  </div>
                  <div>
                    <Typography className={classes.message}>
                      Supported format: .PDF
                    </Typography>
                  </div>
                </div>
              ) : (
                <div>
                  <div className={classes.textboxDisable}>
                    <Icon
                      icon="lets-icons:file-fill"
                      width="16"
                      height="16"
                      color="#29D25D"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={onFileUpload}
                      className="hidden"
                      accept=".pdf"
                    />
                    <button
                      className={classes.fileButtonDisable}
                      onClick={() => fileInputRef.current.click()}
                    >
                      {pdfName}
                    </button>
                    <div className={classes.loading}>
                      <CircularProgress size={16} />
                    </div>
                  </div>
                  <div>
                    <Typography className={classes.message}>
                      Supported format: .PDF
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Button Style</Typography>
        </div>
        <div>
          <Typography className={classes.message}>Icon</Typography>
        </div>
        <div className={classes.boxIconButton}>
          {!selectedIcon ? (
            <CoreIconButton
              color="dark"
              size="small"
              variant="text"
              onClick={onShowIconSelector}
              className={classes.iconButtonAdd}
            >
              <Icon icon={selectedIconName} />
            </CoreIconButton>
          ) : (
            <CoreIconButton
              color="dark"
              size="small"
              variant="text"
              onClick={onShowIconSelector}
              className={classes.iconButtonChange}
            >
              <Icon icon={selectedIconName} />
            </CoreIconButton>
          )}
          <Typography className={classes.header}>
            {!selectedIcon ? 'Add' : 'Change'} Icon
          </Typography>
          {selectedIcon && (
            <CoreIconButton
              color="dark"
              onClick={onIconDelete}
              size="small"
              variant="text"
              className={classes.iconButton}
            >
              <Icon className={classes.sourceIcon} icon="iconoir:trash" />
            </CoreIconButton>
          )}
        </div>
        <div>
          <Typography className={classes.message}>Color</Typography>
        </div>
        <div className={classes.boxIconButton}>
          <div className={classes.inputItem}>
            <div className={classes.pickerBg}>
              <span
                className={classes.pickerSpan}
                style={{
                  backgroundColor: textColor,
                  zIndex: '999999 !important',
                }}
                onClick={event => {
                  setPickerVisibles({
                    ...pickerVisibles,
                    fontColor: !pickerVisibles.fontColor,
                  })
                  setAnchorEl(event.currentTarget)
                }}
              ></span>
              <Popover
                className={classes.popover}
                id={pickerVisibles.fontColor ? 'fontColor' : undefined}
                open={pickerVisibles.fontColor}
                anchorEl={anchorEl}
                onClose={() => {
                  setPickerVisibles({ ...pickerVisibles, fontColor: false })
                  setAnchorEl(null)
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker color={textColor} onChange={onColorChange} />
              </Popover>
            </div>
          </div>
          <Typography className={classes.header}>{textColor}</Typography>
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.boxDelete}>
          <CoreButton
            variant="text"
            color="error"
            className={classes.deleteButton}
            size="small"
            onClick={onDeleteClick}
          >
            <Icon icon="iconoir:trash" />
            <Typography className={classes.deleteTitle}>Delete</Typography>
          </CoreButton>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(ButtonOptionsModal)
